import { Flex, Menu } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { PaymentStatusIndicator } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/PaymentStatusIndicator.tsx';
import { PAYMENT_STATUSES } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/Table.utils.tsx';
import { updateMipPaymentData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/Payments.api.ts';
import { IncompletePaymentDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/IncompletePaymentDrawer.tsx';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { PermissionRole, ResourceType, TransactionState } from '@dealstack/shared/lib/generated';
import { InvestmentPaymentStatusDropdownStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsSidebar/components/InvestmentPaymentCard/InvestmentPaymentCard.styles.tsx';

interface Props {
	investmentWorkflowId: string;
	status: TransactionState;
	annotation: string | null;
	isReleased: boolean;
}

export const InvestmentPaymentStatusDropdown = ({
	investmentWorkflowId,
	status,
	annotation,
	isReleased,
}: Props) => {
	const { mipId } = useRequiredParams('mipId');
	const tMipManage = useTranslationMipManage();
	const { refetch: refetchSummary } = useInvestmentDetailsSummary();
	const [incompleteDrawerOpen, setIncompleteDrawerOpen] = useState(false);

	const updatePaymentRequest = useMutation({
		mutationFn: (variables: Parameters<typeof updateMipPaymentData>[0]) =>
			updateMipPaymentData(variables),
		onSuccess: () => {
			void refetchSummary();
		},
		onError: () => {
			showToastV2({
				title: tMipManage('investment_payment_card.status_update_error'),
				state: 'error',
			});
		},
	});

	const closeIncompleteDrawer = () => setIncompleteDrawerOpen(false);

	const confirmIncomplete = (nextAnnotation: string) => {
		updatePaymentRequest.mutate(
			{
				id: investmentWorkflowId,
				status: TransactionState.INCOMPLETE,
				annotation: nextAnnotation,
			},
			{ onSuccess: closeIncompleteDrawer }
		);
	};

	const handleSelect = (next: TransactionState) => {
		if (next === TransactionState.INCOMPLETE) {
			setIncompleteDrawerOpen(true);
			return;
		}

		updatePaymentRequest.mutate({ id: investmentWorkflowId, status: next });
	};

	if (isReleased) {
		return <PaymentStatusIndicator status={status} />;
	}

	const nextStatuses = PAYMENT_STATUSES.filter((next) => next !== status);

	return (
		<PermissionWrapper
			resourceType={ResourceType.PROGRAMME}
			resourceId={mipId}
			permissionRole={PermissionRole.EDITOR}
			loadingFallback={<PaymentStatusIndicator status={status} />}
		>
			{(hasPermission) => (
				<InvestmentPaymentStatusDropdownStyles>
					<Menu position="bottom-end" disabled={!hasPermission}>
						<Menu.Target>
							<Flex align="center" gap="0.25rem" className="target">
								<PaymentStatusIndicator status={status} textSize={12} />

								<Icon variant="caret-down" size="0.75rem" className="target-icon" />
							</Flex>
						</Menu.Target>

						<Menu.Dropdown miw={200} p="0.25rem">
							<Menu.Label
								className="menu-label"
								p="0.5rem"
								fz={12}
								fw={400}
								c="gray.9"
							>
								{tMipManage('investment_payment_card.change_status')}
							</Menu.Label>

							{nextStatuses.map((next) => (
								<Menu.Item key={next} onClick={() => handleSelect(next)} p="0.5rem">
									<PaymentStatusIndicator status={next} />
								</Menu.Item>
							))}
						</Menu.Dropdown>
					</Menu>

					<IncompletePaymentDrawer
						opened={incompleteDrawerOpen}
						onClose={closeIncompleteDrawer}
						initialAnnotation={annotation}
						isSubmitting={updatePaymentRequest.isPending}
						onConfirm={confirmIncomplete}
					/>
				</InvestmentPaymentStatusDropdownStyles>
			)}
		</PermissionWrapper>
	);
};
